import { AlertTriangle } from 'lucide-react';
import { cn } from '../lib/cn';
import { Button } from './Button';

export interface ErrorStateProps {
  title?: string;
  description?: string;
  /**
   * Request id from the error envelope.
   *
   * Surfacing it means a staff member can quote one short string when reporting
   * a problem, and we can find the exact request in the logs — instead of
   * guessing from "it didn't work this morning".
   */
  requestId?: string;
  onRetry?: () => void;
  className?: string;
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'The page could not be loaded. Please try again.',
  requestId,
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="flex size-12 items-center justify-center rounded-full bg-danger-subtle text-danger-fg"
      >
        <AlertTriangle className="size-6" />
      </span>

      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-text-primary">{title}</h3>
        <p className="max-w-sm text-sm text-text-secondary">{description}</p>
      </div>

      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}

      {requestId ? (
        <p className="text-2xs text-text-tertiary">
          Reference: <code className="font-mono">{requestId}</code>
        </p>
      ) : null}
    </div>
  );
}
